import { c as _c } from "react/compiler-runtime";
import { IconBook } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconBellOutline18, IconBoltOutline18, IconCheckOutline18, IconClockOutline18, IconCrosshairsOutline18, IconGearOutline18 } from "nucleo-ui-outline-18";
import ActionTestPanel from "@/app/(v2)/workflows/_components/ActionTestPanel";
import { Area, AreaChart, ChartTooltip } from "@/components/charts";
import { Button } from "@/components/ui/button";
import { formatNumber } from "@/lib/utils/formatNumber";
import { useAuth } from "@b3dotfun/sdk/global-account/react";
import Link from "next/link";
function formatSparklineTimestamp(bucketStart: string) {
  return new Date(bucketStart).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}
interface IntegrationSidebarProps {
  integrationType: string;
  integration: {
    type: string;
    platform?: string;
    connector?: {
      type: string;
    };
    documentationUrl?: string;
    payloadSchema?: Record<string, unknown>;
  };
  allFields: string[];
  requiredFields: string[];
  resultFields: string[];
  resultRequiredFields: string[];
  nodeStats: {
    isLoading: boolean;
    workflowsCount: number;
    totalExecutions: number;
    successRate: number | null;
    averageLatencyMs: number | null;
  };
  usageTimeSeries: {
    isLoading: boolean;
    data: readonly {
      bucketStart: string;
      usageCount?: number;
    }[];
  };
  latencyTimeSeries: {
    isLoading: boolean;
    data: readonly {
      bucketStart: string;
      avgLatencyMs?: number;
    }[];
  };
  quickTestDefaults: Record<string, unknown>;
  requiresConnector: boolean;
}
export function IntegrationSidebar(t0) {
  const $ = _c(63);
  const {
    integrationType,
    integration,
    allFields,
    requiredFields,
    resultFields,
    resultRequiredFields,
    nodeStats,
    usageTimeSeries,
    latencyTimeSeries,
    quickTestDefaults,
    requiresConnector
  } = t0;
  const {
    isAuthenticated
  } = useAuth();
  const t1 = integrationType === "trigger" ? `/workflows/edit?trigger=${integration?.type || ""}` : `/workflows/edit?action=${integration?.type || ""}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Button size="lg" className="w-full max-w-sm"><span className="flex items-center gap-2"><IconBoltOutline18 className="h-4 w-4" />Use in Workflow</span></Button>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <Link href={t1}>{t2}</Link>;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IconGearOutline18 className="h-4 w-4 flex-shrink-0" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== integration.type) {
    t5 = <div className="body-sm flex items-center gap-3">{t4}<span>ID: <span className="text-ds-text-primary font-mono font-semibold">{integration.type}</span></span></div>;
    $[4] = integration.type;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== integration.platform) {
    t6 = integration.platform && <div className="body-sm flex items-center gap-3"><IconBoltOutline18 className="h-4 w-4 flex-shrink-0" /><span>Platform:{" "}<span className="text-ds-text-primary font-semibold">{integration.platform.charAt(0).toUpperCase() + integration.platform.slice(1)}</span></span></div>;
    $[6] = integration.platform;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconBook className="h-4 w-4 flex-shrink-0" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = allFields.length !== 1 ? "s" : "";
  let t9;
  if ($[9] !== allFields.length || $[10] !== t8) {
    t9 = <span className="text-ds-text-primary font-semibold">{allFields.length} Input{t8}</span>;
    $[9] = allFields.length;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== requiredFields.length) {
    t10 = requiredFields.length > 0 && <span className="text-ds-text-muted">({requiredFields.length} required)</span>;
    $[12] = requiredFields.length;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <div className="body-sm flex items-center gap-3">{t7}<span className="flex items-center gap-1.5">{t9}{t10}</span></div>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== resultFields.length || $[18] !== resultRequiredFields) {
    t12 = resultFields.length > 0 && <div className="body-sm flex items-center gap-3"><IconBook className="h-4 w-4 flex-shrink-0" /><span className="flex items-center gap-1.5"><span className="text-ds-text-primary font-semibold">{resultFields.length} Output{resultFields.length !== 1 ? "s" : ""}</span>{resultRequiredFields.length > 0 && <span className="text-ds-text-muted">({resultRequiredFields.length} required)</span>}</span></div>;
    $[17] = resultFields.length;
    $[18] = resultRequiredFields;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <IconBellOutline18 className="h-4 w-4 flex-shrink-0" />;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = nodeStats.isLoading ? "... active workflows" : `${formatNumber(nodeStats.workflowsCount)} active workflow${nodeStats.workflowsCount !== 1 ? "s" : ""}`;
  let t15;
  if ($[21] !== t14) {
    t15 = <div className="body-sm flex items-center gap-3">{t13}<span>{t14}</span></div>;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== nodeStats.isLoading || $[24] !== nodeStats.successRate) {
    t16 = nodeStats.successRate !== null && <div className="flex items-center gap-3 text-sm text-green-600 dark:text-green-400"><IconCheckOutline18 className="h-4 w-4 flex-shrink-0" /><span>{nodeStats.isLoading ? "..." : `${nodeStats.successRate.toFixed(1)}% success rate`}</span></div>;
    $[23] = nodeStats.isLoading;
    $[24] = nodeStats.successRate;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t11 || $[27] !== t12 || $[28] !== t15 || $[29] !== t16 || $[30] !== t5 || $[31] !== t6) {
    t17 = <div className="space-y-4">{t5}{t6}{t11}{t12}{t15}{t16}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t15;
    $[29] = t16;
    $[30] = t5;
    $[31] = t6;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== nodeStats.isLoading || $[34] !== nodeStats.totalExecutions || $[35] !== usageTimeSeries.data || $[36] !== usageTimeSeries.isLoading) {
    t18 = !usageTimeSeries.isLoading && usageTimeSeries.data.length > 0 && <div className="border-ds-border-default border-t pt-6"><div className="body-sm flex items-center gap-3"><IconCrosshairsOutline18 className="h-4 w-4 flex-shrink-0" /><span className="font-medium">{nodeStats.isLoading ? "..." : formatNumber(nodeStats.totalExecutions)} total execution{nodeStats.totalExecutions !== 1 ? "s" : ""}</span></div><AreaChart data={usageTimeSeries.data as unknown as Record<string, unknown>[]} xDataKey="bucketStart" className="h-24 w-full" aspectRatio="4 / 1" margin={{
        top: 8,
        right: 8,
        bottom: 10,
        left: 8
      }}><Area dataKey="usageCount" fill="var(--chart-line-primary)" stroke="var(--chart-line-primary)" fillOpacity={0.28} strokeWidth={2.25} /><ChartTooltip showDatePill={false} showDots={false} content={_temp} /></AreaChart></div>;
    $[33] = nodeStats.isLoading;
    $[34] = nodeStats.totalExecutions;
    $[35] = usageTimeSeries.data;
    $[36] = usageTimeSeries.isLoading;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== integrationType || $[39] !== latencyTimeSeries || $[40] !== nodeStats.averageLatencyMs || $[41] !== nodeStats.isLoading) {
    t19 = integrationType === "action" && !latencyTimeSeries.isLoading && latencyTimeSeries.data.length > 0 && <div className="space-y-0"><div className="body-sm flex items-center gap-3"><IconClockOutline18 className="h-4 w-4 flex-shrink-0" /><span className="font-medium">{nodeStats.isLoading ? "..." : `${nodeStats.averageLatencyMs?.toFixed(1) || 0}ms`} avg latency</span></div><AreaChart data={latencyTimeSeries.data as unknown as Record<string, unknown>[]} xDataKey="bucketStart" className="h-24 w-full" aspectRatio="4 / 1" margin={{
        top: 8,
        right: 8,
        bottom: 10,
        left: 8
      }}><Area dataKey="avgLatencyMs" fill="var(--chart-line-secondary)" stroke="var(--chart-line-secondary)" fillOpacity={0.24} strokeWidth={2.25} /><ChartTooltip showDatePill={false} showDots={false} content={_temp2} /></AreaChart></div>;
    $[38] = integrationType;
    $[39] = latencyTimeSeries;
    $[40] = nodeStats.averageLatencyMs;
    $[41] = nodeStats.isLoading;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t18 || $[44] !== t19) {
    t20 = <div className="flex flex-col gap-6">{t18}{t19}</div>;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== integration.documentationUrl) {
    t21 = integration.documentationUrl && <div className="border-ds-border-default flex flex-col gap-2 border-t pt-6"><a href={integration.documentationUrl} target="_blank" rel="noopener noreferrer" className="text-ds-text-subtle flex w-full items-center gap-3 text-sm transition-colors hover:text-blue-600 hover:underline"><IconBook className="h-4 w-4" /><span>View documentation</span></a></div>;
    $[46] = integration.documentationUrl;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== integration.connector?.type || $[49] !== integration.type || $[50] !== integrationType || $[51] !== isAuthenticated || $[52] !== quickTestDefaults || $[53] !== requiresConnector) {
    t22 = integrationType === "action" && isAuthenticated && <div className="border-ds-border-default border-t pt-6"><h3 className="body-sm-semibold mb-3 flex items-center gap-2"><IconBoltOutline18 className="h-4 w-4" />Quick Test</h3><ActionTestPanel actionType={integration.type} defaultInputs={quickTestDefaults} connectorType={integration.connector?.type} connectorRequired={requiresConnector} /></div>;
    $[48] = integration.connector?.type;
    $[49] = integration.type;
    $[50] = integrationType;
    $[51] = isAuthenticated;
    $[52] = quickTestDefaults;
    $[53] = requiresConnector;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t17 || $[56] !== t20 || $[57] !== t21 || $[58] !== t22) {
    t23 = <div className="mt-8 space-y-6">{t17}{t20}{t21}{t22}</div>;
    $[55] = t17;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t23 || $[61] !== t3) {
    t24 = <>{t3}{t23}</>;
    $[60] = t23;
    $[61] = t3;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  return t24;
}
function _temp2(t0) {
  const {
    point: point_0
  } = t0;
  return <div className="px-3 py-2.5"><p className="text-chart-tooltip-foreground text-xs font-medium">{formatSparklineTimestamp(String(point_0.bucketStart ?? ""))}</p><div className="mt-2 flex items-center justify-between gap-4"><span className="text-chart-tooltip-muted text-sm">Latency</span><span className="text-chart-tooltip-foreground text-sm font-semibold tabular-nums">{`${Number(point_0.avgLatencyMs ?? 0).toFixed(1)} ms`}</span></div></div>;
}
function _temp(t0) {
  const {
    point
  } = t0;
  return <div className="px-3 py-2.5"><p className="text-chart-tooltip-foreground text-xs font-medium">{formatSparklineTimestamp(String(point.bucketStart ?? ""))}</p><div className="mt-2 flex items-center justify-between gap-4"><span className="text-chart-tooltip-muted text-sm">Executions</span><span className="text-chart-tooltip-foreground text-sm font-semibold tabular-nums">{formatNumber(Number(point.usageCount ?? 0))}</span></div></div>;
}